/**
 * Crypto utilities for key management
 * Platform-agnostic - works in both browser and React Native
 */

const KEY_STORAGE_NAME = 'myagent_private_key'

/**
 * Generate random bytes
 */
const randomBytes = (length = 32) => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    // Browser and modern environments
    return crypto.getRandomValues(new Uint8Array(length))
  }
  // Fallback (should not happen in modern browsers/RN)
  throw new Error('crypto.getRandomValues not available')
}

/**
 * Convert bytes to hex string
 */
export const bytesToHex = (bytes: Uint8Array) => {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert hex string to bytes
 */
export const hexToBytes = (hex: string) => {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substr(i, 2), 16)
  }
  return bytes
}

/**
 * Storage adapter - allows different storage backends
 */
let storageAdapter: any = {
  getItem: (key: string) => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key)
    }
    return null
  },
  setItem: (key: string, value: string) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value)
    }
  },
  removeItem: (key: string) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key)
    }
  },
}

/**
 * Configure storage adapter
 * Use this for React Native AsyncStorage or other storage backends
 */
export const configureStorage = (adapter: any) => {
  storageAdapter = adapter
}

/**
 * Get or generate private key
 * Stored in configured storage for persistence across sessions
 */
export const getPrivateKey = () => {
  const existing = storageAdapter.getItem(KEY_STORAGE_NAME)
  if (!existing) {
    const bytes = randomBytes(32)
    const hex = bytesToHex(bytes)
    storageAdapter.setItem(KEY_STORAGE_NAME, hex)
    console.log('[Crypto] Generated new private key')
    return hex
  }
  return existing
}

/**
 * Get public key from client instance in hex format
 */
export const getPublicKey = (client: any) => {
  if (!client) {
    return 'not_connected'
  }

  const publicKey = client.publicKey
  if (!publicKey) {
    return 'connecting...'
  }

  // Convert Uint8Array to hex string
  if (publicKey instanceof Uint8Array) {
    return bytesToHex(publicKey)
  }

  // If it's already a string and looks like base64, convert to hex
  if (typeof publicKey === 'string') {
    if (publicKey.includes('+') || publicKey.includes('/') || publicKey.includes('=')) {
      try {
        const bytes = Uint8Array.from(atob(publicKey), c => c.charCodeAt(0))
        return bytesToHex(bytes)
      } catch (e) {
        console.warn('[Crypto] Failed to convert public key to hex:', e)
        return publicKey
      }
    }
    return publicKey.toLowerCase()
  }

  return String(publicKey)
}

/**
 * Reset key (clear from storage)
 * Note: Application should handle reload/restart after this
 */
export const resetKey = () => {
  try {
    storageAdapter.removeItem(KEY_STORAGE_NAME)
    console.log('[Crypto] Key reset successfully')
    return true
  } catch (error) {
    console.error('[Crypto] Failed to reset key:', error)
    return false
  }
}

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      console.log('[Crypto] Copied to clipboard')
      return true
    }
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  } catch (error) {
    console.error('[Crypto] Failed to copy:', error)
    return false
  }
}
